import HeroCircles from './hero-circles';

const settings = {
    // Adjusts the overall speed of the timeline, if necessary
    timeScale: 1.0,

    classNames: {
        container: 'hero-homepage',
        preload: 'hero-homepage--preload',
        paused: 'hero-homepage--paused',
    },

    selectors: {
        logo: '.hero-homepage__logo',
        playButton: '.hero-homepage__play-controls button',
        pauseContainer: '.hero-homepage__pause-controls',
        pauseButton: '.hero-homepage__pause-controls button',
    },
};

export default class HomepageHero extends HeroCircles {
    constructor(container) {
        // Call the base class constructor
        super(
            container,
            false,
        );

        // Locate our DOM elements
        this.playButton = container
            .querySelector(settings.selectors.playButton);
        this.pauseContainer = container
            .querySelector(settings.selectors.pauseContainer);
        this.pauseButton = container
            .querySelector(settings.selectors.pauseButton);

        // Adjust timeline playback rate
        this.timeline.timeScale(settings.timeScale);

        // Setup play button
        this.playButton.addEventListener(
            'click',
            (event) => {
                event.preventDefault();

                // Do nothing if we're already playing
                if (!this.timeline.paused()) {
                    return;
                }

                // Play the timeline
                this.timeline.play();
            },
        );

        // Setup pause button
        this.pauseButton.addEventListener(
            'click',
            (event) => {
                event.preventDefault();

                if (this.timeline.paused()) {
                    // Resume playback
                    container.classList.remove(settings.classNames.paused);
                    this.timeline.play();
                } else {
                    // Pause
                    container.classList.add(settings.classNames.paused);
                    this.timeline.pause();
                }
            },
        );

        // Add show transition for the pause button, mimicing the animation
        // for the "start" circle screen
        const introScreen = this.screens[0];
        const startScreen = this.screens[1];
        const startTime = startScreen.timestamps.start + 0.25;
        this.timeline.to(
            this.pauseContainer,
            1.0,
            {
                ease: 'power2.out',
                display: 'block',
                opacity: 1,
                scale: 1,
            },
            startTime,
        );

        // Disable play button once it's no longer visible
        this.timeline.to(
            this.playButton,
            1.0,
            {
                ease: 'power3.out',
                display: 'none',
            },
            introScreen.timestamps.end - 1.0,
        );

        // ...fade it out more quickly than the rest of the content on this screen, too
        this.timeline.to(
            this.playButton,
            1.0,
            {
                ease: 'power3.out',
                opacity: 0,
            },
            introScreen.timestamps.end - 1.0,
        );

        // Add final fade out & reset
        const endScreen = this.screens[this.screens.length - 1];
        const endTime = endScreen.timestamps.end + 2.0;
        this.timeline.to(
            [
                this.background.container,
                endScreen.container,
                this.pauseContainer,
            ],
            1.0,
            {
                ease: 'power2.out',
                opacity: 0,
            },
            endTime,
        );

        // Disable pause button during fade out
        this.timeline.to(
            this.pauseButton,
            1.0,
            {
                ease: 'power2.out',
                pointerEvents: 'none',
            },
            endTime,
        );

        // Re-show start screen
        introScreen.show(
            this.timeline,
            endTime + 0.75,
        );

        // Re-enable play button
        this.timeline.to(
            this.playButton,
            1.0,
            {
                ease: 'power2.out',
                display: 'block',
                opacity: 1,
            },
            endTime + 0.75,
        );

        // Rewind to the beginning once complete
        this.timeline.add(() => {
            this.timeline.pause();
            this.timeline.progress(0);
        });

        // Try to determine the logo image URL
        let logoUrl = null;
        const logo = container.querySelector(settings.selectors.logo);
        if (logo) {
            const logoStyles = window.getComputedStyle(logo);
            if (logoStyles.backgroundImage) {
                const match = /url\([\"\'](?<url>.+)[\"\']\)/
                    .exec(logoStyles.backgroundImage);

                if (match) {
                    logoUrl = match.groups.url;
                }
            }
        }

        let revealHeroTriggered = false;
        const revealHero = () => {
            if (revealHeroTriggered) {
                return;
            }

            revealHeroTriggered = true;
            container.classList.remove(settings.classNames.preload);
        };

        if (!logoUrl) {
            // Couldn't determine the logo image URL - just reveal
            // the content when the DOMContentLoaded event fires
            document.addEventListener('DOMContentLoaded', revealHero);

            // Stop here
            return;
        }

        // Reveal the content when the image has loaded, or after
        // a brief interval as a fallback
        const logoImage = new Image();
        logoImage.addEventListener('load', revealHero);
        logoImage.src = logoUrl;

        setTimeout(revealHero, 500);
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .getElementsByClassName(settings.classNames.container);

        for (let i=0; i<elements.length; i++) {
            new HomepageHero(elements[i]);
        }
    }
}
